import { useEffect, useMemo, useState } from "react";
import * as Y from "yjs";
import { IndexeddbPersistence } from "y-indexeddb";
import {
  type DomainMaps,
  getDomainMaps,
  putSheet,
  type Sheet,
  type SheetId,
} from "@electro-planner/domain";

type Cached = {
  doc: Y.Doc;
  maps: DomainMaps;
  persistence: IndexeddbPersistence;
  syncedPromise: Promise<void>;
};

// Fas 1: en Y.Doc per documentId, persisterad lokalt via y-indexeddb.
// Cache:n lever över React-remounts så vi inte öppnar/stänger persistens onödigt —
// IndexeddbPersistence är idempotent per documentId men det kostar runda-turer.
const CACHE = new Map<string, Cached>();

export type UseYjsDocResult = {
  doc: Y.Doc;
  maps: DomainMaps;
  synced: boolean;
};

function getOrCreate(
  documentId: string,
  defaultSheet: { id: SheetId; title: string },
): Cached {
  const existing = CACHE.get(documentId);
  if (existing) return existing;

  const doc = new Y.Doc();
  const maps = getDomainMaps(doc);
  const persistence = new IndexeddbPersistence(
    `electro-planner:${documentId}`,
    doc,
  );

  // Seed default-sheet först efter att IndexedDB har synkat — annars
  // skulle vi dubblera när användaren redan har skapat ark lokalt.
  // Utanför LOCAL_ORIGIN eftersom initial state inte är en user-mutation.
  const syncedPromise = persistence.whenSynced.then(() => {
    if (maps.sheets.size === 0) {
      doc.transact(() => {
        const sheet: Sheet = {
          id: defaultSheet.id,
          index: 0,
          title: defaultSheet.title,
          format: "A3",
          orientation: "landscape",
        };
        putSheet(maps, sheet);
        maps.sheetOrder.insert(0, [sheet.id]);
      });
    }
  });

  const entry: Cached = { doc, maps, persistence, syncedPromise };
  CACHE.set(documentId, entry);
  return entry;
}

export function useYjsDoc(
  documentId: string,
  defaultSheet: { id: SheetId; title: string },
): UseYjsDocResult {
  const cached = useMemo(
    () => getOrCreate(documentId, defaultSheet),
    [documentId, defaultSheet.id, defaultSheet.title],
  );
  const [synced, setSynced] = useState(false);

  useEffect(() => {
    let cancelled = false;
    cached.syncedPromise.then(() => {
      if (!cancelled) setSynced(true);
    });
    return () => {
      cancelled = true;
    };
  }, [cached]);

  return { doc: cached.doc, maps: cached.maps, synced };
}
